import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const GettingStartedGuide = () => {
  const [activeUserType, setActiveUserType] = useState('customer');
  const [expandedSection, setExpandedSection] = useState(null);

  const customerGuide = [
    {
      id: 'browsing',
      title: 'Browsing the Model Catalog',
      icon: 'Search',
      steps: [
        'Visit the Model Catalog from the main navigation menu',
        'Use the search bar to find specific products or browse by category',
        'Apply filters for price range, manufacturer, or product features',
        'Click on any model card to view detailed 3D previews and specifications'
      ]
    },
    {
      id: 'viewing',
      title: 'Using the 3D Model Viewer',
      icon: 'Eye',
      steps: [
        'Click on any product to open the interactive 3D viewer',
        'Use your mouse to rotate, zoom, and pan around the model',
        'Toggle measurement tools to check dimensions and specifications',
        'View different components by selecting them from the component list',
        'Save favorites by clicking the heart icon for quick access later'
      ]
    },
    {
      id: 'purchasing',
      title: 'Making a Purchase',
      icon: 'ShoppingCart',
      steps: [
        'Add desired products to your cart from the model viewer or catalog',
        'Review your cart and verify compatibility warnings if any',
        'Proceed to checkout and enter shipping information',
        'Select your preferred payment method',
        'Review order summary and confirm purchase',
        'Track your order status from your account dashboard'
      ]
    },
    {
      id: 'account',
      title: 'Managing Your Account',
      icon: 'User',
      steps: [
        'Access your dashboard from the user menu in the top right',
        'Update profile information including shipping addresses',
        'Add or manage payment methods securely',
        'View purchase history and download invoices',
        'Track active orders and delivery status',
        'Manage your wishlist and saved products'
      ]
    }
  ];

  const sellerGuide = [
    {
      id: 'signup',
      title: 'Creating a Seller Account',
      icon: 'UserPlus',
      steps: [
        'Click "Become a Seller" from the main navigation or homepage',
        'Fill out the seller registration form with your business details',
        'Provide company information including tax ID and business address',
        'Upload verification documents (business license, tax forms)',
        'Agree to seller terms and conditions',
        'Wait for account approval (typically 24-48 hours)',
        'Once approved, access your seller dashboard to start listing products'
      ],
      tips: [
        'Ensure all business documents are current and valid',
        'Use a professional email address for your seller account',
        'Complete your seller profile thoroughly to build trust with customers'
      ]
    },
    {
      id: 'upload-model',
      title: 'Uploading a 3D Model',
      icon: 'Upload',
      steps: [
        'Navigate to your Seller Dashboard',
        'Click "Add New Product" from the product management section',
        'Fill in basic product information (name, description, category)',
        'Upload your 3D model file (supported formats: GLB, GLTF)',
        'Ensure your model file is under 50MB for optimal performance',
        'Add product images and thumbnails (recommended: 1200x800px)',
        'Set model visibility and component naming for interactive features',
        'Preview your model in the 3D viewer before publishing',
        'Click "Save Draft" to review later or "Publish" to make it live'
      ],
      tips: [
        'Optimize your 3D models before uploading to reduce file size',
        'Use descriptive component names within your model for better user experience',
        'Test your model in the preview viewer to ensure proper rendering',
        'Include high-quality product images from multiple angles'
      ]
    },
    {
      id: 'add-products',
      title: 'Adding Products (Basic Account)',
      icon: 'Package',
      steps: [
        'From your Seller Dashboard, go to "Product Management"',
        'Click "Add New Product" button',
        'Enter product details: Name, SKU, Price, and Description',
        'Select appropriate category and tags for better discoverability',
        'Upload 3D model file (required) and product images',
        'Set pricing: Base price, discount options, and bulk pricing if applicable',
        'Add technical specifications (dimensions, weight, materials)',
        'Set inventory quantity and stock management preferences',
        'Configure shipping options and estimated delivery times',
        'Review all information and click "Publish Product"'
      ],
      limitations: [
        'Basic accounts can list up to 50 products',
        'Maximum 3D model file size: 50MB per product',
        'Standard listing features included (no featured placements)',
        'Access to basic analytics and sales reports'
      ],
      tips: [
        'Write detailed, SEO-friendly product descriptions',
        'Use all available image slots (minimum 3 images recommended)',
        'Keep inventory quantities updated to prevent overselling',
        'Set competitive prices by researching similar products'
      ]
    },
    {
      id: 'manage-products',
      title: 'Managing Your Products',
      icon: 'Settings',
      steps: [
        'Access product management from your Seller Dashboard',
        'View all products in list or grid view',
        'Edit product details by clicking the edit icon',
        'Update inventory levels and pricing as needed',
        'Monitor product performance with built-in analytics',
        'Respond to customer questions and reviews',
        'Pause or unpublish products that are out of stock',
        'Duplicate existing products to create similar listings quickly'
      ]
    },
    {
      id: 'orders',
      title: 'Processing Orders',
      icon: 'TruckIcon',
      steps: [
        'Check your dashboard for new orders (you\'ll receive email notifications)',
        'Review order details including customer information and shipping address',
        'Prepare products for shipment within the specified processing time',
        'Generate shipping labels through our integrated shipping partners',
        'Update order status to "Shipped" and add tracking information',
        'Monitor delivery status and respond to customer inquiries',
        'Confirm delivery completion and request customer feedback'
      ]
    },
    {
      id: 'analytics',
      title: 'Understanding Analytics',
      icon: 'BarChart',
      steps: [
        'Navigate to "Analytics" section in your Seller Dashboard',
        'View key metrics: Total sales, views, conversion rate',
        'Analyze product performance to identify best sellers',
        'Track customer demographics and buying patterns',
        'Monitor traffic sources to optimize marketing efforts',
        'Review revenue reports and payment history',
        'Export data for external analysis or tax purposes'
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const currentGuide = activeUserType === 'customer' ? customerGuide : sellerGuide;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <Header />
      
      <div className="container mx-auto px-6 pt-24 pb-16">
        {/* Header Section */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
            Getting Started Guide
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Everything you need to know to get started on our platform
          </p>
        </div>

        {/* User Type Selector */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-white rounded-2xl p-2 shadow-lg border border-slate-200">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setActiveUserType('customer')}
                className={`py-4 px-6 rounded-xl font-semibold transition-all ${
                  activeUserType === 'customer' ?'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md' :'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="ShoppingBag" size={20} />
                  <span>Customer Guide</span>
                </div>
              </button>
              <button
                onClick={() => setActiveUserType('seller')}
                className={`py-4 px-6 rounded-xl font-semibold transition-all ${
                  activeUserType === 'seller' ?'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md' :'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="Store" size={20} />
                  <span>Seller Guide</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Guide Content */}
        <div className="max-w-4xl mx-auto space-y-6">
          {currentGuide?.map((section) => (
            <div
              key={section?.id}
              className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section?.id)}
                className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                    <Icon name={section?.icon} className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    {section?.title}
                  </h2>
                </div>
                <Icon
                  name={expandedSection === section?.id ? 'ChevronUp' : 'ChevronDown'}
                  size={24}
                  className="text-slate-400"
                />
              </button>

              {expandedSection === section?.id && (
                <div className="px-6 pb-6 space-y-6">
                  <div className="pl-4 border-l-4 border-blue-500">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Step-by-Step Instructions
                    </h3>
                    <ol className="space-y-3">
                      {section?.steps?.map((step, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-semibold text-sm">
                            {index + 1}
                          </span>
                          <span className="text-slate-700 pt-1">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  {section?.tips && (
                    <div className="pl-4 border-l-4 border-green-500">
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                        <Icon name="Lightbulb" size={20} className="text-green-600" />
                        <span>Pro Tips</span>
                      </h3>
                      <ul className="space-y-2">
                        {section?.tips?.map((tip, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="CheckCircle" size={18} className="text-green-600 mt-1 flex-shrink-0" />
                            <span className="text-slate-700">{tip}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {section?.limitations && (
                    <div className="pl-4 border-l-4 border-amber-500">
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center space-x-2">
                        <Icon name="AlertCircle" size={20} className="text-amber-600" />
                        <span>Basic Account Limitations</span>
                      </h3>
                      <ul className="space-y-2">
                        {section?.limitations?.map((limitation, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <Icon name="Info" size={18} className="text-amber-600 mt-1 flex-shrink-0" />
                            <span className="text-slate-700">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional Resources */}
        <div className="max-w-4xl mx-auto mt-16 bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Need More Help?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="/support-center"
              className="p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <Icon name="HelpCircle" size={32} className="text-blue-600 mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2">Support Center</h3>
              <p className="text-sm text-slate-600">Get help from our support team</p>
            </a>
            
            <a
              href="/resources-hub"
              className="p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <Icon name="BookOpen" size={32} className="text-purple-600 mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2">Documentation</h3>
              <p className="text-sm text-slate-600">Browse our complete documentation</p>
            </a>
            
            <a
              href="#"
              className="p-6 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <Icon name="Video" size={32} className="text-pink-600 mb-3" />
              <h3 className="font-semibold text-slate-800 mb-2">Video Tutorials</h3>
              <p className="text-sm text-slate-600">Watch step-by-step video guides</p>
            </a>
          </div>
        </div>

        {/* CTA Section */}
        {activeUserType === 'seller' && (
          <div className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Start Selling?</h2>
            <p className="text-xl text-blue-100 mb-6">
              Join thousands of sellers making money with 3D models
            </p>
            <Button variant="default" className="bg-white text-blue-600 hover:bg-blue-50">
              Create Seller Account
            </Button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default GettingStartedGuide;